import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/Button";
import { ChipRow } from "../../ui/ChipRow";
import { RatingRow, type RatingValue } from "../../ui/RatingRow";
import { Textarea } from "../../ui/Textarea";
import type { AnnotationVerdict } from "../../../domains/gpt/stores/annotationSheetStore";

type SourceDirection = "off_topic" | "too_few" | "outdated";
type LengthDirection = "too_long" | "too_short";

/**
 * Every answer the form can hold, all nullable: "not yet answered" is a real
 * state and must not read as a 1. Form state lives in React Hook Form rather than
 * `useState` — the house rule for business components.
 */
type RubricFormValues = {
  answerRelevance: RatingValue | null;
  sourceRelevance: RatingValue | null;
  sourceDirection: SourceDirection | null;
  answerOrder: RatingValue | null;
  answerLength: RatingValue | null;
  lengthDirection: LengthDirection | null;
  readability: RatingValue | null;
  sourceFidelity: "grounded" | "partial" | "unsupported" | null;
  clinicalRisk: "none" | "flagged" | null;
  remark: string;
};

/**
 * A rating with its direction, in the shape the rubric makes representable: a high
 * score carries no direction, a low one carries exactly one.
 *
 * Returns `null` for a combination the contract forbids, which is what keeps the
 * submit button honest — it is disabled by the absence of a verdict, not by a
 * second copy of the validation rules living next to them.
 *
 * The band is checked with `===` rather than `>=` so TypeScript narrows the
 * literal union: `1 | 2 | 3` in the low arm falls out by exclusion.
 */
function directed<D extends string>(
  rating: RatingValue | null,
  direction: D | null
):
  | { rating: 4 | 5; direction: null }
  | { rating: 1 | 2 | 3; direction: D }
  | null {
  if (rating === null) return null;
  if (rating === 4 || rating === 5) {
    return direction === null ? { rating, direction: null } : null;
  }
  return direction === null ? null : { rating, direction };
}

/** The verdict this form describes, or `null` while it is still incomplete. */
function buildVerdict(form: RubricFormValues): AnnotationVerdict | null {
  const sourceRelevance = directed(form.sourceRelevance, form.sourceDirection);
  const answerLength = directed(form.answerLength, form.lengthDirection);
  if (
    form.answerRelevance === null ||
    sourceRelevance === null ||
    form.answerOrder === null ||
    answerLength === null ||
    form.readability === null ||
    form.sourceFidelity === null ||
    form.clinicalRisk === null
  ) {
    return null;
  }
  return {
    kind: "scored",
    scaleVersion: 1,
    answerRelevance: { rating: form.answerRelevance },
    sourceRelevance,
    answerOrder: { rating: form.answerOrder },
    answerLength,
    readability: { rating: form.readability },
    sourceFidelity: form.sourceFidelity,
    clinicalRisk: form.clinicalRisk,
    // "" and "no remark" are the same thing to a reader; store the absence.
    remark: form.remark.trim() === "" ? null : form.remark.trim(),
  };
}

/** A stored verdict, back as form values — a revision opens on what was said. */
function toValues(existing: AnnotationVerdict | null): RubricFormValues {
  const blank: RubricFormValues = {
    answerRelevance: null,
    sourceRelevance: null,
    sourceDirection: null,
    answerOrder: null,
    answerLength: null,
    lengthDirection: null,
    readability: null,
    sourceFidelity: null,
    clinicalRisk: null,
    remark: "",
  };
  if (existing === null || existing.kind !== "scored") return blank;
  return {
    answerRelevance: existing.answerRelevance.rating,
    sourceRelevance: existing.sourceRelevance.rating,
    sourceDirection: existing.sourceRelevance.direction,
    answerOrder: existing.answerOrder.rating,
    answerLength: existing.answerLength.rating,
    lengthDirection: existing.answerLength.direction,
    readability: existing.readability.rating,
    sourceFidelity: existing.sourceFidelity,
    clinicalRisk: existing.clinicalRisk,
    remark: existing.remark === null ? "" : existing.remark,
  };
}

/**
 * The seven criteria, in ONE copy.
 *
 * Both phases use it: our answer, and a pasted competitor answer. That is not
 * code tidiness — "a criterion missing on one side makes the gap unreadable", so
 * the rubric has to be identical for the Juisci answer and the compared one,
 * without exception. A second, slightly different copy for phase 2 would produce
 * a comparison of two different measurements.
 *
 * `children` is where a caller puts what belongs ABOVE the criteria (the answer
 * being judged, or the tool picker and paste box), so the scroll container and
 * the sticky footer stay owned here.
 */
export function RubricForm({
  initial,
  submitLabel,
  secondaryLabel,
  onSecondary,
  onSubmit,
  children,
}: {
  initial: AnnotationVerdict | null;
  submitLabel: string;
  // Required-nullable rather than optional: a caller has to decide whether this
  // form has a second action, and phase 2 does not.
  secondaryLabel: string | null;
  onSecondary: (() => void) | null;
  onSubmit: (verdict: AnnotationVerdict) => Promise<void>;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { watch, setValue, handleSubmit, formState, register } =
    useForm<RubricFormValues>({ defaultValues: toValues(initial) });

  const form = watch();
  const verdict = buildVerdict(form);
  const lowSources = form.sourceRelevance !== null && form.sourceRelevance <= 3;
  const lowLength = form.answerLength !== null && form.answerLength <= 3;

  return (
    // `min-h-full` + a `flex-1` content area, and NO overflow of our own: the
    // sheet's own content slot is already the scroll container (`BottomPanel`,
    // `overflow-y-auto min-h-0`). Nesting a second one gave two overlapping
    // scrollbars and, worse, left the footer at the END of the content instead of
    // pinned — you had to scroll past seven criteria before the buttons existed.
    <form
      className="flex min-h-full flex-col"
      onSubmit={handleSubmit(async () => {
        // Non-null here: the submit button is disabled until it is.
        if (verdict === null) return;
        await onSubmit(verdict);
      })}
    >
      <div className="flex flex-1 flex-col gap-6 px-5 py-6 ">
        {children}

        <RatingRow
          value={form.answerRelevance}
          onChange={(value) => {
            setValue("answerRelevance", value);
          }}
          label={t("Answer relevance")}
          description={t("How well it matches the question asked")}
          minLabel={t("Not at all satisfactory")}
          maxLabel={t("Entirely satisfactory")}
        />

        <div className="flex flex-col gap-2">
          <RatingRow
            value={form.sourceRelevance}
            onChange={(value) => {
              setValue("sourceRelevance", value);
              // Leaving the low band drops a direction that is no longer
              // representable: the contract forbids a 5 that also says "too few",
              // so the form must not hold one either.
              if (value >= 4) setValue("sourceDirection", null);
            }}
            label={t("Source relevance")}
            description={t(
              "Are the cited references the right ones for this question?"
            )}
            minLabel={null}
            maxLabel={null}
          />
          {lowSources && (
            <ChipRow
              value={form.sourceDirection}
              onChange={(value) => {
                setValue("sourceDirection", value);
              }}
              label={t("Which way")}
              items={[
                { value: "off_topic", label: t("Off-topic sources") },
                { value: "too_few", label: t("Too few sources") },
                { value: "outdated", label: t("Sources too old") },
              ]}
            />
          )}
        </div>

        <RatingRow
          value={form.answerOrder}
          onChange={(value) => {
            setValue("answerOrder", value);
          }}
          label={t("Answer order")}
          description={t(
            "Structure and logic of how the information is presented"
          )}
          minLabel={null}
          maxLabel={null}
        />

        <div className="flex flex-col gap-2">
          <RatingRow
            value={form.answerLength}
            onChange={(value) => {
              setValue("answerLength", value);
              if (value >= 4) setValue("lengthDirection", null);
            }}
            label={t("Answer length")}
            description={t("Neither too short nor too long for the need")}
            minLabel={null}
            maxLabel={null}
          />
          {lowLength && (
            <ChipRow
              value={form.lengthDirection}
              onChange={(value) => {
                setValue("lengthDirection", value);
              }}
              label={t("Which way")}
              items={[
                { value: "too_long", label: t("Too long") },
                { value: "too_short", label: t("Too short") },
              ]}
            />
          )}
        </div>

        <RatingRow
          value={form.readability}
          onChange={(value) => {
            setValue("readability", value);
          }}
          label={t("Readability")}
          description={t("Clarity of language, formatting")}
          minLabel={null}
          maxLabel={null}
        />

        {/* The two off-scale criteria. A claim is supported by a cited source or
            it is not — "faithful, 3 out of 5" means nothing, and it is the only
            criterion that catches a hallucination — and a clinical risk is raised
            rather than scored, because a 1-out-of-5 in a dashboard wakes nobody. */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5 font-dm-sans">
            <span className="text-ui-body font-semibold text-ds-moss-800">
              {t("Source fidelity")}
            </span>
            <span className="text-ui-note font-normal text-ds-slate-600">
              {t("Is every claim supported by a cited source?")}
            </span>
          </div>
          <ChipRow
            value={form.sourceFidelity}
            onChange={(value) => {
              setValue("sourceFidelity", value);
            }}
            label={t("Source fidelity")}
            items={[
              { value: "grounded", label: t("Grounded") },
              { value: "partial", label: t("Partial") },
              { value: "unsupported", label: t("Unsupported") },
            ]}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-dm-sans text-ui-body font-semibold text-ds-moss-800">
            {t("Clinical risk")}
          </span>
          <ChipRow
            value={form.clinicalRisk}
            onChange={(value) => {
              setValue("clinicalRisk", value);
            }}
            label={t("Clinical risk")}
            items={[
              { value: "none", label: t("None") },
              { value: "flagged", label: t("Flag a risk") },
            ]}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-dm-sans text-ui-body font-semibold text-ds-moss-800">
            {t("Free remark")}
          </span>
          <Textarea
            placeholder={t("What the ratings do not say…")}
            {...register("remark")}
          />
        </div>
      </div>

      {/* `sticky`, so it is on screen from the first paint and stays there while
          the criteria scroll under it. It sticks to the bottom of the SHEET's
          scroll box, which only works because nothing between here and it sets
          `overflow` any more. */}
      <div className="sticky bottom-0 z-10 flex shrink-0 gap-3 border-t border-ds-slate-200 bg-white px-5 py-4">
        {secondaryLabel === null || onSecondary === null ? null : (
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onSecondary}
          >
            {secondaryLabel}
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          className="flex-1"
          disabled={verdict === null || formState.isSubmitting}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
